import { Wallet, AlertTriangle, Users, CalendarClock, Flag, ClipboardList, Clock, Landmark } from 'lucide-react';
import type { ArStats } from '../../types/arDunning';
import { formatMoney } from '../../utils/arDunningFormat';

// Tailwind needs literal class names — map the possible tile counts to a static class.
const LG_COLS: Record<number, string> = { 6: 'lg:grid-cols-6', 7: 'lg:grid-cols-7', 8: 'lg:grid-cols-8' };

interface Props {
    stats: ArStats | null;
    onPromisesClick?: () => void;
    onDisputesClick?: () => void;
    onFollowUpsClick?: () => void;
    onOverdueClick?: () => void;
}

/**
 * Server-aggregated KPI tiles (never computed from a paginated page). Amounts are
 * EUR-consolidated — the currency toggle only affects per-row figures in the lists.
 */
export function ArKpiTiles({ stats, onPromisesClick, onDisputesClick, onFollowUpsClick, onOverdueClick }: Props) {
    const overduePct = stats && stats.totalOpenEUR > 0 ? Math.round((stats.overdueEUR / stats.totalOpenEUR) * 100) : 0;
    // DSO is central-only (sellers get null from the API) — show the tile only when present.
    const showDso = stats?.dsoDays != null;
    // Issued RiBa (SBF) is a dit-only credit-exposure figure — show the tile only when present,
    // so other companies/scopes see an unchanged tile row.
    const showRiba = stats != null && stats.ribaExposureEUR > 0;

    const tiles = [
        { label: 'Open (net)', value: formatMoney(stats?.totalOpenEUR ?? 0), sub: `${stats?.openItemCount ?? 0} items`, icon: Wallet, tone: 'text-slate-700' },
        { label: 'Overdue', value: formatMoney(stats?.overdueEUR ?? 0), sub: `${stats?.overdueCount ?? 0} items · ${overduePct}%`, icon: AlertTriangle, tone: 'text-rose-700', onClick: onOverdueClick },
        ...(showRiba ? [{ label: 'RiBa (SBF)', value: formatMoney(stats!.ribaExposureEUR), sub: `${stats!.ribaItemCount} items · bank-collected`, icon: Landmark, tone: 'text-blue-700' }] : []),
        ...(showDso ? [{ label: 'DSO', value: `${stats!.dsoDays} d`, sub: 'days sales outstanding', icon: Clock, tone: 'text-indigo-700' }] : []),
        { label: 'Customers', value: String(stats?.customerCount ?? 0), sub: 'with open items', icon: Users, tone: 'text-slate-700' },
        { label: 'Open promises', value: String(stats?.openPromiseCount ?? 0), sub: 'promise-to-pay', icon: CalendarClock, tone: 'text-sky-700', onClick: onPromisesClick },
        { label: 'Disputes', value: String(stats?.openDisputeCount ?? 0), sub: 'on hold', icon: Flag, tone: 'text-amber-700', onClick: onDisputesClick },
        { label: 'Follow-ups', value: String(stats?.openFollowUpCount ?? 0), sub: 'open tasks', icon: ClipboardList, tone: 'text-slate-700', onClick: onFollowUpsClick },
    ];

    return (
        <div className={`grid grid-cols-2 gap-3 md:grid-cols-3 ${LG_COLS[tiles.length] ?? 'lg:grid-cols-6'}`}>
            {tiles.map((t) => {
                const Icon = t.icon;
                const clickable = !!t.onClick;
                return (
                    <div
                        key={t.label}
                        onClick={t.onClick}
                        className={`rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${clickable ? 'cursor-pointer hover:border-indigo-300' : ''}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{t.label}</span>
                            <Icon className={`h-4 w-4 ${t.tone}`} />
                        </div>
                        <div className={`mt-1 text-lg font-semibold ${t.tone}`}>{t.value}</div>
                        <div className="text-xs text-slate-400">{t.sub}</div>
                    </div>
                );
            })}
        </div>
    );
}
